import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { MdFilterAlt } from 'react-icons/md';
import { LuHourglass } from 'react-icons/lu';
import { DateRangePicker, GroupedCheckboxDropdown } from 'components/common';
import SalesUnitGroupedDropdown from 'components/app/crm/shared/SalesUnitGroupedDropdown';
import { CRM_CO_STATUS } from 'app_config';
import { CO_STATUS_MAP } from './Column';

const CO_STATUS_GROUPS = [{
  key:   'status',
  label: 'CO Status',
  color: '#6366f1',
  items: CRM_CO_STATUS.map((s) => ({
    value: s,
    label: CO_STATUS_MAP[s]?.label ?? (s.charAt(0).toUpperCase() + s.slice(1)),
  })),
}];

const Divider = () => <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />;

/**
 * The filter bar of the Project Schedule (OE) detail grid. `countLabel` is the
 * grid's "N change orders" total.
 */
const ScheduleFilterBar = ({ filters, countLabel, onOpenDrawer }) => {
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);
  const { buildingStatusData = [] } = useSelector((s) => s.crm_setting);

  const buildingStatusGroups = useMemo(() => [{
    key:   'building_status',
    label: 'Building Status',
    color: '#0ea5e9',
    items: (buildingStatusData || []).map((s) => ({ value: s.id, label: s.name })),
  }], [buildingStatusData]);

  const { filterTags } = filters;

  return (
    <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">

        <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-44 sm:tw-flex-shrink-0">
          <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => filters.setSearch(e.target.value)}
            placeholder="Q#, J#, project name..."
            className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
          />
          {filters.search && (
            <button type="button" onClick={filters.clearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
              <FaTimes size={8} />
            </button>
          )}
        </div>

        <Divider />

        {/* Outer div keeps the hover title; the inner one blocks the picker. */}
        <div
          title={filters.pendingRevisionOnly ? 'Entry Date is ignored while "REV# waiting CSD" is on' : undefined}
          aria-disabled={filters.pendingRevisionOnly}
        >
          <div className={filters.pendingRevisionOnly ? 'tw-opacity-40 tw-pointer-events-none' : undefined}>
            <DateRangePicker
              label="Entry Date"
              startDate={filters.entryDate.startDate}
              endDate={filters.entryDate.endDate}
              onChange={filters.setEntryDate}
            />
          </div>
        </div>

        <Divider />

        <SalesUnitGroupedDropdown
          units={activeSalesUnitsData}
          selectedIds={filters.salesUnitIds}
          onChange={filters.setSalesUnitIds}
          placeholder="All Units"
          width={220}
        />

        <Divider />

        <GroupedCheckboxDropdown
          groups={CO_STATUS_GROUPS}
          selectedIds={filters.coStatusIds}
          onChange={filters.setCoStatusIds}
          placeholder="CO Status"
          width={200}
          showGroupLabels={false}
        />

        {/* CSD worklist: CO#s whose latest REV# still needs accept/reject. */}
        <button
          type="button"
          onClick={filters.togglePendingRevision}
          aria-pressed={filters.pendingRevisionOnly}
          title="Show only CO#s whose latest REV# is waiting for CSD to accept/reject"
          className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
            filters.pendingRevisionOnly
              ? 'tw-bg-amber-500 tw-border-amber-500 tw-text-white hover:tw-bg-amber-600'
              : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-amber-300 hover:tw-text-amber-700 hover:tw-bg-amber-50'
          }`}
        >
          <LuHourglass size={14} />
          REV# waiting CSD
        </button>

        <Divider />

        <GroupedCheckboxDropdown
          groups={buildingStatusGroups}
          selectedIds={filters.buildingStatusIds}
          onChange={filters.setBuildingStatusIds}
          placeholder="Building Status"
          width={220}
          showGroupLabels={false}
        />

        <Divider />

        <button
          type="button"
          onClick={onOpenDrawer}
          className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
            filterTags.length > 0
              ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
              : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
          }`}
        >
          <MdFilterAlt size={15} />
          More Filters
          {filterTags.length > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
              {filterTags.length}
            </span>
          )}
        </button>

        {countLabel && (
          <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
            {countLabel}
          </span>
        )}
      </div>

      {filterTags.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
          <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
          {filterTags.map((tag) => (
            <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
              <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
              <span className="tw-text-slate-300 tw-mx-0.5">·</span>
              <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">{Array.isArray(tag.value) ? tag.value.join(' – ') : tag.value}</span>
              <button type="button" onClick={() => filters.removeTag(tag)} className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors">
                <FaTimes size={8} />
              </button>
            </span>
          ))}
          <button type="button" onClick={filters.resetFilter} className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-text-red-500 hover:tw-bg-red-500 hover:tw-text-white tw-border tw-border-red-200 hover:tw-border-red-500 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-font-medium">
            <FaTimes size={8} />
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

ScheduleFilterBar.propTypes = {
  filters:      PropTypes.object.isRequired,
  countLabel:   PropTypes.string,
  onOpenDrawer: PropTypes.func.isRequired,
};

export default ScheduleFilterBar;
